import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import AccountContext from '../../../services/AccountContext';
import CollectionsContext from '../../../services/CollectionsContext';
import childrenService from '../../../services/children';

import styles from './WorksheetActions.module.less';
import Action from './Action';

const AssignmentsModal = dynamic(() => import('../../AssignmentsModal'), {
  ssr: false,
});

const GradeWorksheetModal = dynamic(() => import('../GradeWorksheetModal'), {
  ssr: false,
});

const SaveToDrive = dynamic(() => import('./SaveToDrive'), { ssr: false });

import { openFeatureWall } from '../../../services/payWallModal';

function WorksheetActions({
  id,
  thumbnail,
  title,
  lessonPlan,
  slideshow,
  answersheetHref,
  trackDownload,
  downloadHref,
}) {
  const [gradeWorksheetIsOpen, setGradeWorksheetIsOpen] = useState(false);
  const [assignmentsIsOpen, setAssignmentsIsOpen] = useState(false);

  const { isPremium, ready } = useContext(AccountContext);
  const { isCollected, collectContent } = useContext(CollectionsContext);

  //TODO use player/kids context instead of  this once it's merged
  const [numChildren, setNumChildren] = useState(0);
  useEffect(() => {
    childrenService.fetch().then(res => {
      setNumChildren(res.children.length);
    });
  }, []);

  const handleClickCollect = () => {
    collectContent('worksheet', id, {
      type: 'worksheet',
      id,
      thumbnail,
      title,
    });
  };

  const handleAnswersClick = e => {
    if (isPremium) {
      trackDownload && trackDownload({ answerKey: true });
      return;
    } else if (!ready) {
      return;
    }

    e.preventDefault();
    showFeatureWall(
      10.575,
      'Unlock Answer Sheets',
      '<p>Answer Sheets are available to Premium members only.</p><p>Upgrade to Premium membership to access answer sheets and over 20,000 worksheets.</p>',
      'Answer Key Download'
    );
  };

  const showFeatureWall = (cid, featureWallTitle, text, feature) => {
    openFeatureWall({
      contentType: 'worksheet',
      contentId: id,
      cid,
      title: featureWallTitle,
      text,
      name: title,
      feature,
      section: 'Worksheet Detail',
    });
  };

  const gradeWorksheetEnabled = isPremium && numChildren > 0;
  const inCollection = isCollected('worksheet', id);

  return (
    <div>
      <div className={styles.actionsContainer}>
        {lessonPlan && (
          <Action
            text="See in a Lesson Plan"
            icon="set"
            href={`/lesson-plan/${lessonPlan}/`}
            target="_blank"
          />
        )}
        {!lessonPlan && slideshow ? (
          <Action
            text={`See in a set (${slideshow.count})`}
            icon="set"
            href={`/slideshow/${slideshow.url}/`}
            data-cid={10.571}
            title={`Set: ${slideshow.title}`}
            target="_blank"
            rel="noopener noreferrer"
          />
        ) : (
          ''
        )}
        {answersheetHref && (
          <Action
            href={answersheetHref}
            icon="check"
            text="View answers"
            onClick={handleAnswersClick}
            target="_blank"
            rel="noopener noreferrer"
          />
        )}
        <Action
          icon={inCollection ? 'bookmark-filled' : 'bookmark'}
          text={inCollection ? 'Added to collection' : 'Add to collection'}
          onClick={handleClickCollect}
        />
        <Action
          icon="child-face"
          text="Assign digitally"
          onClick={() => setAssignmentsIsOpen(true)}
        />
        {gradeWorksheetEnabled ? (
          <Action
            icon="pencil"
            text="Grade worksheet"
            onClick={() => {
              setGradeWorksheetIsOpen(true);
            }}
          />
        ) : (
          ''
        )}
        <SaveToDrive
          className={styles.saveToDrive}
          name={title}
          contentType="worksheet"
          contentTypeDisplayName="Worksheet"
          downloadHref={downloadHref}
        />
      </div>
      {gradeWorksheetIsOpen ? (
        <GradeWorksheetModal
          contentid={id}
          open={gradeWorksheetIsOpen}
          setOpen={setGradeWorksheetIsOpen}
        />
      ) : null}
      <AssignmentsModal
        onClose={() => setAssignmentsIsOpen(false)}
        isOpen={assignmentsIsOpen}
        itemsToAdd={[{ type: 'worksheet', internalName: id }]}
        contentId={id}
        section="Worksheet Detail"
      />
    </div>
  );
}

WorksheetActions.propTypes = {
  title: PropTypes.string,
  thumbnail: PropTypes.string,
  id: PropTypes.string,
  lessonPlan: PropTypes.string,
  slideshow: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
    count: PropTypes.number,
  }),
  trackDownload: PropTypes.func,
  answersheetHref: PropTypes.string,
  downloadHref: PropTypes.string,
};

export default WorksheetActions;
